// eslint-disable-next-line import/default
import type PopperJS from '@popperjs/core';
import { useTrackers } from '@ps/web-shared-analytics';
import { Link, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import he from 'he';
import maxSize from 'popper-max-size-modifier';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { maxHeight } from '@swf/core/utils/popperModifiers';
import type { Article } from '@swf/help';

import Reference from '../Reference/Reference';

import { active, article, resultList, searchResult, searchSection } from './SearchBar.scss';
import { useSaveQuickSearch } from './hooks';

export interface Props {
  usedSearchTerm?: string;
}

interface PopperParams {
  state: PopperJS.State;
  instance: PopperJS.Instance;
}

const SearchBar: React.FC<Props> = ({ usedSearchTerm = '' }) => {
  const [searchTerm, setSearchTerm] = useState(usedSearchTerm);
  const [searchResults, setSearchResults] = useState([] as Article[]);
  const [showArticleList, setShowArticleList] = useState(false);
  const [selected, setSelected] = useState<number | undefined>();
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { windowSize } = useScreenSize();
  const userId = useSelector(select.account.getUserId);
  const analyticsPath = useSelector(select.location.getAnalyticsPath);
  const { pageView } = useTrackers();

  const searchParams = useMemo(() => {
    const params = new URLSearchParams();
    if (userId && searchTerm) params.append('searchTerm', searchTerm);
    return params;
  }, [searchTerm, userId]);

  const modifiers = useMemo(
    () => [
      {
        name: 'sameWidth',
        enabled: showArticleList,
        fn: ({ state }: PopperParams) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        phase: 'beforeWrite' as const
      },
      {
        name: 'preventOverflow',
        options: { rootBoundary: 'document' }
      },
      maxSize,
      maxHeight({ bottomSpacing: 15 })
    ],
    [showArticleList]
  );

  const clear = useCallback(() => {
    setShowArticleList(false);
    setSelected(undefined);
  }, [setSelected, setShowArticleList]);

  const selectArticle = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      setSearchTerm('');
      clear();
    },
    [clear]
  );

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers
  });

  useEffect(() => pageView(analyticsPath as string), [analyticsPath, pageView]);

  useEffect(() => {
    windowSize && searchResults && showArticleList && update && update();
  }, [windowSize, searchResults, showArticleList, update, searchTerm, setSearchTerm]);

  useEffect(() => {
    setSearchTerm(usedSearchTerm);
  }, [usedSearchTerm]);

  const handleClickOutside = useCallback(
    (e: Event) => {
      if (
        referenceElement?.contains(e.target as Node) ||
        popperElement?.contains(e.target as Node)
      ) {
        if (searchTerm) {
          setShowArticleList(true);
        }
        return;
      }
      setSelected(undefined);
      setShowArticleList(false);
    },
    [popperElement, referenceElement, searchTerm]
  );

  useSaveQuickSearch();

  useEffect(() => {
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  return (
    <div className={searchSection}>
      <Reference
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setShowArticleList={setShowArticleList}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        selected={selected}
        setSelected={setSelected}
        setReferenceElement={setReferenceElement}
        onClear={clear}
      />
      {searchResults && (
        <div
          className={searchResult}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          {searchResults?.length > 0 && showArticleList && (
            <ul
              className={resultList}
              onMouseLeave={() => setSelected(undefined)}
              data-testid="search-results"
            >
              {searchResults.map(({ id, ref, title }, index) => (
                <li
                  key={id}
                  className={classNames(article, selected === index && active)}
                  onClick={selectArticle}
                  onMouseMove={() => setSelected(index)}
                  onFocus={() => setSelected(index)}
                >
                  <Link alias="helpCenterArticle" values={{ ref }} searchParams={searchParams}>
                    {he.decode(title)}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
